import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

/** An entry in the page-button row: a page number, or an ellipsis gap. */
export type PageItem = number | 'ellipsis';

/**
 * Pagination control: a "Showing start–end of total" summary plus prev/next and
 * windowed page buttons (always 1, last, and current ±1, with ellipses for
 * gaps). Renders nothing when there's only a single page.
 *
 * The page count is derived from `total` / `perPage`, so the control adapts to
 * wherever it's used — just pass the total item count and the page size. It's
 * generic and presentational: it emits `pageChange` with the requested 1-based
 * page and the consumer fetches the data.
 *
 * @example
 * <fb-pagination
 *   [page]="page()" [total]="total()" [perPage]="perPage()"
 *   label="projects" (pageChange)="page.set($event)" />
 */
@Component({
  selector: 'fb-pagination',
  templateUrl: './pagination.html',
  styleUrl: './pagination.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Pagination {
  /** Current 1-based page. */
  readonly page = input(1);

  /** Total number of items across all pages. */
  readonly total = input(0);

  /** Items per page. */
  readonly perPage = input(0);

  /** Noun shown in the summary, e.g. "projects". */
  readonly label = input('items');

  /** Emits the requested 1-based page. */
  readonly pageChange = output<number>();

  /** Page count, derived from the total item count and the page size. */
  protected readonly totalPages = computed(() => {
    const perPage = this.perPage();
    return perPage > 0 ? Math.ceil(this.total() / perPage) : 0;
  });

  protected readonly visible = computed(() => this.totalPages() > 1);

  protected readonly start = computed(() => (this.page() - 1) * this.perPage() + 1);
  protected readonly end = computed(() => Math.min(this.page() * this.perPage(), this.total()));

  protected readonly items = computed<PageItem[]>(() => {
    const last = this.totalPages();
    const current = this.page();
    const wanted = [1, last, current - 1, current, current + 1].filter(
      (pageNumber) => pageNumber >= 1 && pageNumber <= last,
    );
    const unique = [...new Set(wanted)].sort((first, second) => first - second);

    const result: PageItem[] = [];
    let previous = 0;
    for (const pageNumber of unique) {
      if (pageNumber - previous > 1) {
        result.push('ellipsis');
      }
      result.push(pageNumber);
      previous = pageNumber;
    }
    return result;
  });

  protected go(pageNumber: number): void {
    if (pageNumber >= 1 && pageNumber <= this.totalPages() && pageNumber !== this.page()) {
      this.pageChange.emit(pageNumber);
    }
  }
}
